// src/pages/markets/api/useSignals.ts

import { useState, useEffect, useRef } from 'react';
import { apiGet } from './apiClient';
import type { SignalItem, MarketSignalsResponse } from './types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface EventSignalsResponse {
    event_id: string;
    signals: SignalItem[];
    signal_count: number;
    open_signal_count: number;
}

export interface MarketSignalsResult {
    signals: SignalItem[];
    loading: boolean;
    error: Error | null;
}

export interface EventSignalsResult {
    signals: SignalItem[];
    loading: boolean;
    error: Error | null;
    openCount: number;
}

// ---------------------------------------------------------------------------
// Cache
// ---------------------------------------------------------------------------

const PAGE_SIZE = 500;
const signalsCache = new Map<string, SignalItem[]>();

// ---------------------------------------------------------------------------
// fetchAllSignalsForMarket
//
// API always returns DESC (newest-first). Pagination uses to_ts
// (opened_at < X), which is exclusive — no boundary overlap, no dedup needed.
// Cursor = opened_at of the last (oldest) item on each page.
// Pages are collected in DESC order then reversed so the cache is ASC.
// ---------------------------------------------------------------------------

async function fetchAllSignalsForMarket(
    conditionId: string,
    abortSignal: AbortSignal,
): Promise<SignalItem[]> {
    const cached = signalsCache.get(conditionId);
    if (cached) {
        console.debug(`[fetchAllSignals] cache hit for ${conditionId} — ${cached.length} signals`);
        return cached;
    }

    console.debug(`[fetchAllSignals] starting fetch for ${conditionId}, PAGE_SIZE=${PAGE_SIZE}`);

    const pages: SignalItem[][] = [];
    let toTs: string | null = null;
    let pageNum = 0;

    while (true) {
        if (abortSignal.aborted) throw new DOMException('Aborted', 'AbortError');

        pageNum++;
        const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
        if (toTs) params.set('to_ts', toTs);

        const url = `/api/signals/${encodeURIComponent(conditionId)}?${params}`;
        console.debug(`[fetchAllSignals] page ${pageNum} — GET ${url}`);

        let page: MarketSignalsResponse;
        try {
            page = await apiGet<MarketSignalsResponse>(url, abortSignal);
        } catch (err) {
            console.error(`[fetchAllSignals] page ${pageNum} FAILED for ${conditionId}:`, err);
            throw err;
        }

        const { signals: pageSignals } = page;
        console.debug(
            `[fetchAllSignals] page ${pageNum} — ${pageSignals.length} signals` +
            (pageSignals.length > 0
                ? `, newest=${pageSignals[0].opened_at}, oldest=${pageSignals[pageSignals.length - 1].opened_at}`
                : ''),
        );

        pages.push(pageSignals);

        // Partial page → we've reached the end
        if (pageSignals.length < PAGE_SIZE) {
            console.debug(`[fetchAllSignals] partial page (${pageSignals.length} < ${PAGE_SIZE}) — done`);
            break;
        }

        // Cursor = oldest item on this page (last in DESC order), exclusive on next request
        const oldestOnPage = pageSignals[pageSignals.length - 1].opened_at;

        // Safety: cursor didn't move — bail out to avoid infinite loop
        if (oldestOnPage === toTs) {
            console.warn(`[fetchAllSignals] cursor stalled at ${toTs} — breaking`);
            break;
        }

        toTs = oldestOnPage;
        console.debug(`[fetchAllSignals] next cursor: to_ts=${toTs}`);
    }

    // Pages arrived newest-first; reverse both the page order and each page
    // so the final array is ASC (oldest-first), consistent with chart expectations.
    const all = pages.reverse().flatMap(p => [...p].reverse());

    console.info(`[fetchAllSignals] DONE for ${conditionId} — ${all.length} signals across ${pageNum} page(s)`);
    signalsCache.set(conditionId, all);
    return all;
}

// ---------------------------------------------------------------------------
// useMarketSignals
//
// Fetches all signals for a single market. API returns DESC but the cache
// and returned array are ASC (oldest-first). Callers that need newest-first
// (e.g. SignalsPanel) should reverse a copy: [...signals].reverse()
// ---------------------------------------------------------------------------

export function useMarketSignals(marketId: string | null): MarketSignalsResult {
    const [signals, setSignals] = useState<SignalItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const abortRef = useRef<AbortController | null>(null);

    useEffect(() => {
        if (!marketId) {
            setSignals([]);
            setLoading(false);
            setError(null);
            return;
        }

        abortRef.current?.abort();
        const ctrl = new AbortController();
        abortRef.current = ctrl;

        console.debug(`[useMarketSignals] starting for marketId=${marketId}`);
        setLoading(true);
        setError(null);

        fetchAllSignalsForMarket(marketId, ctrl.signal)
            .then((data) => {
                if (ctrl.signal.aborted) return;
                console.info(`[useMarketSignals] resolved ${data.length} signals for ${marketId}`);
                setSignals(data);
                setLoading(false);
            })
            .catch((err: Error) => {
                if (ctrl.signal.aborted || err?.name === 'AbortError') return;
                console.error('[useMarketSignals] fetch failed:', err);
                setError(err);
                setLoading(false);
            });

        return () => {
            console.debug(`[useMarketSignals] aborting for ${marketId}`);
            ctrl.abort();
        };
    }, [marketId]);

    return { signals, loading, error };
}

// ---------------------------------------------------------------------------
// useEventSignals
//
// Fetches the most recent N signals across ALL markets of an event.
// Single request, no pagination. Result is ASC.
// ---------------------------------------------------------------------------

export function useEventSignals(
    eventId: string | null | undefined,
    limit = 50,
    marketIds?: string[],
): EventSignalsResult {
    const [signals, setSignals] = useState<SignalItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [openCount, setOpenCount] = useState(0);

    const abortRef = useRef<AbortController | null>(null);

    useEffect(() => {
        if (!eventId) {
            setSignals([]);
            setLoading(false);
            setError(null);
            setOpenCount(0);
            return;
        }

        abortRef.current?.abort();
        const ctrl = new AbortController();
        abortRef.current = ctrl;

        setLoading(true);
        setError(null);

        const params = new URLSearchParams({ limit: String(limit) });
        if (marketIds && marketIds.length > 0) {
            params.set('markets', marketIds.join(','));
        }

        const url = `/api/signals/event/${encodeURIComponent(eventId)}?${params}`;
        console.debug(`[useEventSignals] GET ${url}`);

        apiGet<EventSignalsResponse>(url, ctrl.signal)
            .then((data) => {
                if (ctrl.signal.aborted) return;
                const fetched = data.signals ?? [];
                console.info(
                    `[useEventSignals] ${fetched.length} signals for event ${eventId}` +
                    ` (open: ${data.open_signal_count ?? 0})`,
                );
                setSignals(fetched);
                setOpenCount(data.open_signal_count ?? 0);
                setLoading(false);
            })
            .catch((err: Error) => {
                if (ctrl.signal.aborted || err?.name === 'AbortError') return;
                console.error('[useEventSignals] fetch failed:', err);
                setError(err);
                setLoading(false);
            });

        return () => {
            console.debug(`[useEventSignals] aborting for event ${eventId}`);
            ctrl.abort();
        };
    }, [eventId, limit, marketIds?.join(',')]);

    return { signals, loading, error, openCount };
}

// ---------------------------------------------------------------------------
// Cache helpers
// ---------------------------------------------------------------------------

export function invalidateSignalsCache(conditionId?: string): void {
    if (conditionId) {
        console.debug(`[signalsCache] invalidating ${conditionId}`);
        signalsCache.delete(conditionId);
    } else {
        console.debug(`[signalsCache] clearing all (${signalsCache.size} entries)`);
        signalsCache.clear();
    }
}